import { useQueryClient } from '@tanstack/react-query';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { LocationList, LocationListFooter } from '@/components/location';
import { queryKeys } from '@/lib/react-query';
import { Location } from '@/models';
import { showMessage } from '@/utils';
import { useLocations } from '@/web-api/locations';
import { useSetMyLocations } from '@/web-api/resources';

const { Text } = Typography;

type ResourceLocationsProps = {
  resourceLocations?: Location[];
};

export const ResourceLocations = ({ resourceLocations }: ResourceLocationsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: allLocations } = useLocations();
  const setMyLocations = useSetMyLocations();

  const onAddLocations = (locationIds: string[]) => {
    const currentLocationIds = resourceLocations?.map((l) => l.id) ?? [];
    const newLocationIds = [...currentLocationIds, ...locationIds];
    setMyLocations.mutate(newLocationIds, {
      onSuccess: () => {
        showMessage('success', t('forms:saved'));
        void queryClient.invalidateQueries({
          queryKey: queryKeys.resources.me(),
        });
      },
    });
  };

  const onRemoveLocation = (locationId: string) => {
    const currentLocationIds = resourceLocations?.map((l) => l.id) ?? [];
    const newLocationIds = currentLocationIds.filter((id) => id !== locationId);
    setMyLocations.mutate(newLocationIds, {
      onSuccess: () => {
        showMessage('success', t('forms:saved'));
        void queryClient.invalidateQueries({
          queryKey: queryKeys.resources.me(),
        });
      },
    });
  };

  const availableLocations = resourceLocations?.length
    ? allLocations?.filter(
        (location) => !resourceLocations?.map((rl) => rl.id).includes(location.id)
      )
    : allLocations;

  return (
    <div>
      <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
        {t('common:locationsHeader')}
      </Text>
      <LocationList
        locations={resourceLocations}
        onRemove={onRemoveLocation}
        isLoading={false}
        footer={<LocationListFooter locations={availableLocations} onSubmit={onAddLocations} />}
      />
    </div>
  );
};
